import React from 'react';
import isPromise from 'is-promise';
import noop from 'lodash/noop';
import isString from 'lodash/isString';
import { MessageManager } from 'choerodon-ui/shared';
import Icon from '../icon';
import Progress from '../progress';
import { ProgressType } from '../progress/enum';
import { Size } from '../_util/enum';
import { newNotificationInstance } from '../notification/Notification';
import { getPlacementStyle, getPlacementTransitionName } from './util';
import { getPrefixCls } from '../configure/utils';
const { config } = MessageManager;
function getCustomizePrefixCls() {
    return getPrefixCls('message', config.prefixCls);
}
function getMessageInstance(placement, callback, contentClassName) {
    const { instance } = MessageManager;
    if (instance) {
        if (isPromise(instance)) {
            instance.then(callback);
            return instance;
        }
        callback(instance);
        return Promise.resolve(instance);
    }
    const promise = new Promise((resolve) => {
        newNotificationInstance({
            prefixCls: getCustomizePrefixCls(),
            style: getPlacementStyle(placement, config.top, config.bottom),
            transitionName: getPlacementTransitionName(placement, config.transitionName),
            getContainer: config.getContainer,
            contentClassName,
            maxCount: config.maxCount,
        }, (instance) => {
            resolve(instance);
            MessageManager.instance = instance;
            callback(instance);
        });
    });
    MessageManager.instance = promise;
    return promise;
}
function notice(content, duration = config.duration, type, onClose, placement) {
    const iconType = config.icons[type];
    if (isString(onClose)) {
        placement = onClose;
        onClose = noop;
    }
    if (typeof duration === 'function') {
        onClose = duration;
        duration = config.duration;
    }
    else if (isString(duration)) {
        placement = duration;
    }
    const target = MessageManager.getUuid();
    const prefixCls = getCustomizePrefixCls();
    const icon = iconType === 'loading' ?
        React.createElement(Progress, { type: ProgressType.loading, size: Size.small }) :
        React.createElement(Icon, { type: iconType });
    let promise;
    const closePromise = new Promise(resolve => {
        promise = getMessageInstance(placement || config.placement, instance => {
            instance.notice({
                key: target,
                duration,
                style: {},
                contentClassName: `${prefixCls}-content-${type}`,
                children: (React.createElement("div", { className: `${prefixCls}-custom-content ${prefixCls}-${type}` },
                    icon,
                    React.createElement("span", null, content))),
                onClose() {
                    if (typeof onClose === 'function') {
                        onClose();
                    }
                    resolve();
                },
            });
        }, `${prefixCls}-content-${type}`);
    });
    const result = () => {
        if (promise) {
            promise.then((ins) => ins.removeNotice(target));
        }
    };
    result.then = (filled, rejected) => closePromise.then(filled, rejected);
    result.promise = closePromise;
    return result;
}
export default {
    info(content, duration, onClose, placement) {
        return notice(content, duration, 'info', onClose, placement);
    },
    success(content, duration, onClose, placement) {
        return notice(content, duration, 'success', onClose, placement);
    },
    error(content, duration, onClose, placement) {
        return notice(content, duration, 'error', onClose, placement);
    },
    // Departed usage, please use warning()
    warn(content, duration, onClose, placement) {
        return notice(content, duration, 'warning', onClose, placement);
    },
    warning(content, duration, onClose, placement) {
        return notice(content, duration, 'warning', onClose, placement);
    },
    loading(content, duration, onClose, placement) {
        return notice(content, duration, 'loading', onClose, placement);
    },
    config(options) {
        if (options.top !== undefined) {
            config.top = options.top;
            MessageManager.clear();
        }
        if (options.bottom !== undefined) {
            config.bottom = options.bottom;
            MessageManager.clear();
        }
        if (options.duration !== undefined) {
            config.duration = options.duration;
        }
        if (options.prefixCls !== undefined) {
            config.prefixCls = options.prefixCls;
        }
        if (options.getContainer !== undefined) {
            config.getContainer = options.getContainer;
        }
        if (options.transitionName !== undefined) {
            config.transitionName = options.transitionName;
            MessageManager.clear();
        }
        if (options.placement !== undefined) {
            config.placement = options.placement;
        }
        if (options.maxCount !== undefined) {
            config.maxCount = options.maxCount;
        }
        if (options.icons !== undefined) {
            config.icons = {
                ...config.icons,
                ...options.icons,
            };
        }
    },
    destroy() {
        MessageManager.clear();
    },
};
